import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Statistic,
  Descriptions,
  Tag,
  Radio,
  Table
} from 'antd';
import {
  ReloadOutlined,
  ApiOutlined,
  KeyOutlined,
  CloudServerOutlined,
  DatabaseOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { App as AntApp } from 'antd';
import { admin } from '../../services/api';

const { Title, Text } = Typography;

const IntegracoesDashboard = () => {
  const { message } = AntApp.useApp();
  const [loading, setLoading] = useState(false);
  const [sswConfig, setSswConfig] = useState(null);
  const [sswStatus, setSswStatus] = useState(null);
  const [sswAccessCount, setSswAccessCount] = useState(0);
  const [integrations, setIntegrations] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [sswSessions, setSswSessions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    await Promise.allSettled([
      admin.getSswConfig().then(setSswConfig).catch(() => setSswConfig(null)),
      admin.sswStatus().then(setSswStatus).catch(() => setSswStatus(null)),
      admin.getUsers().then((users) => setSswAccessCount((users || []).filter((u) => u.erpUsuario && u.erpUsuario.trim() !== '').length)).catch(() => setSswAccessCount(0)),
      admin.getOrgIntegrations().then(setIntegrations).catch(() => setIntegrations([])),
      admin.getApiKeys().then(setApiKeys).catch(() => setApiKeys([])),
      admin.sswSessions().then(setSswSessions).catch(() => setSswSessions([]))
    ]);
    setLoading(false);
  }, []);

  // Não existe mais config-grupo SSW (credenciais org-level). `buscaPath`
  // continua sendo uma preferência da organização e é o único campo gravado.
  const changeBuscaPath = async (e) => {
    const path = e.target.value;
    try {
      setLoading(true);
      await admin.upsertSswConfig({ buscaPath: path });
      message.success(`Caminho alterado para: ${path === 'database' ? 'Banco Local' : 'SSW Service'}`);
      load();
    } catch (e) {
      message.error(e.message || 'Erro ao alterar caminho');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2} style={{ margin: 0 }}>Dashboard de Integrações</Title>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Atualizar</Button>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading} size="small">
            <Statistic
              title="Integrações"
              value={integrations.length}
              valueStyle={{ fontSize: 14 }}
              prefix={<ApiOutlined />}
              suffix={integrations.length === 1 ? 'ativa' : 'ativas'}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading} size="small">
            <Statistic
              title="API Keys"
              value={apiKeys.length}
              valueStyle={{ fontSize: 14 }}
              prefix={<KeyOutlined />}
              suffix={apiKeys.length === 1 ? 'chave' : 'chaves'}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading} size="small">
            <Statistic
              title="Acessos ao ERP"
              value={sswAccessCount}
              valueStyle={{ fontSize: 14, color: sswAccessCount > 0 ? '#52c41a' : '#ff4d4f' }}
              prefix={<CloudServerOutlined />}
              suffix={sswAccessCount === 1 ? 'acesso' : 'acessos'}
            />
          </Card>
        </Col>
      </Row>

      <Card title="Caminho da Busca de Notas Fiscais" style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            Define como a aplicação busca os dados da nota fiscal ao escanear ou digitar a chave de acesso no modal de inclusão.
          </Text>
        </div>
        <Radio.Group value={sswConfig?.buscaPath || 'database'} onChange={changeBuscaPath} disabled={loading}>
          <Radio.Button value="database"><DatabaseOutlined /> Banco Local</Radio.Button>
          <Radio.Button value="ssw"><CloudServerOutlined /> SSW Service</Radio.Button>
        </Radio.Group>
        <div style={{ marginTop: 12 }}>
          {sswConfig?.buscaPath === 'ssw'
            ? <Tag color="blue">Buscando dados via SSW Service — requer navegador logado e configuração na aba SSW</Tag>
            : <Tag color="green">Buscando dados no banco local</Tag>}
        </div>
        {!sswConfig && (
          <div style={{ marginTop: 8 }}>
            <Text type="warning">Nenhuma configuração SSW encontrada. O caminho padrão é "Banco Local".</Text>
          </div>
        )}
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title={<><CloudServerOutlined /> Acessos SSW</>} size="small" loading={loading}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Usuários com acesso">
                {sswAccessCount > 0 ? (
                  <Tag color="green">{sswAccessCount} acesso(s) por usuário + filial</Tag>
                ) : (
                  <Tag color="red">Nenhum</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={sswStatus?.estado === 'LOGADO' ? 'green' : 'orange'}>{sswStatus?.estado || 'Desconectado'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Logado">
                <Tag color={sswStatus?.logado ? 'green' : 'red'}>{sswStatus?.logado ? 'Sim' : 'Não'}</Tag>
              </Descriptions.Item>
            </Descriptions>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                O acesso ao ERP é por usuário/filial (aba Integrações → Acessos SSW). Não existe mais configuração geral.
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title={<><GlobalOutlined /> Sessões no ERP</>}
            size="small"
            style={{ marginTop: 16 }}
            loading={loading}
            extra={<Tag color="blue">{sswSessions.length} sessão(ões)</Tag>}
          >
            <Table
              rowKey="sessionKey"
              size="small"
              dataSource={sswSessions}
              pagination={false}
              locale={{ emptyText: 'Nenhuma sessão/cookie do ERP SSW encontrada.' }}
              columns={[
                {
                  title: 'Usuário',
                  dataIndex: 'usuario',
                  ellipsis: true,
                  render: (v, record) => v
                    ? (
                      <div>
                        <div>{v}</div>
                        {record.erpCpf && (
                          <Text type="secondary" style={{ fontSize: 12 }}>CPF: {record.erpCpf}</Text>
                        )}
                      </div>
                    )
                    : '-',
                },
                {
                  title: 'Filial',
                  dataIndex: 'filial',
                  ellipsis: true,
                  render: (v) => v || '-',
                },
                {
                  title: 'Qtd. Cookies',
                  dataIndex: 'cookieCount',
                  align: 'center',
                  render: (v) => <Tag color={v > 0 ? 'green' : 'red'}>{v}</Tag>,
                },
                {
                  title: 'Atualizada em',
                  dataIndex: 'createdAt',
                  render: (v) =>
                    v ? new Date(v).toLocaleString('pt-BR') : '-',
                },
              ]}
              expandable={{
                expandedRowRender: (record) => (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Cookies válidos no navegador (Playwright) para acesso ao ERP:
                    </Text>
                    {record.cookies && record.cookies.length > 0 ? (
                      <Table
                        rowKey={(c, i) => `${record.sessionKey}-${c.name}-${c.domain}-${i}`}
                        size="small"
                        pagination={false}
                        dataSource={record.cookies}
                        columns={[
                          {
                            title: 'Nome',
                            dataIndex: 'name',
                            ellipsis: true,
                            render: (v) => v || '-',
                          },
                          {
                            title: 'Domínio',
                            dataIndex: 'domain',
                            ellipsis: true,
                            render: (v) => v || '-',
                          },
                          {
                            title: 'Tipo',
                            dataIndex: 'session',
                            render: (v) =>
                              v ? <Tag>Cookie de sessão</Tag> : <Tag color="blue">Persistente</Tag>,
                          },
                          {
                            title: 'Expira',
                            dataIndex: 'expires',
                            render: (v) =>
                              v && v > 0
                                ? new Date(v * 1000).toLocaleString('pt-BR')
                                : (v === 0 || v === -1 ? 'Sessão' : '-'),
                          },
                        ]}
                      />
                    ) : (
                      <Text type="secondary">Sem cookies salvos.</Text>
                    )}
                  </div>
                ),
              }}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Sessões e cookies gravados pelo Playwright para o acesso ao ERP SSW (storageState por usuário/filial).
              </Text>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default IntegracoesDashboard;
